<script setup lang="ts">
interface Props {
    widgetId: string;
    label: string;
    color?: string;
    isToggle?: boolean;
    icon?: string;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    color: '#00f0ff',
    isToggle: false,
    icon: undefined,
    disabled: false,
});

const emit = defineEmits<{
    (e: 'change', value: number): void;
}>();

const { qlcStore, pressButton, releaseButton, toggleButton } = useQlcPlus();

const isActive = computed(() => {
    return (qlcStore.widgets[props.widgetId]?.value ?? 0) > 0;
});

const isHeld = ref(false);

function handleClick(): void {
    if (props.disabled) {
        return;
    }
    if (props.isToggle) {
        toggleButton(props.widgetId);
        emit('change', isActive.value ? 255 : 0);
    }
}

function handlePointerDown(event: PointerEvent): void {
    if (props.disabled || props.isToggle) {
        return;
    }

    // Momentary / Flash mode
    isHeld.value = true;
    try {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    }
    catch {
        // Ignore pointer capture errors on older browsers
    }
    pressButton(props.widgetId);
    emit('change', 255);
}

function handlePointerUp(event: PointerEvent): void {
    if (props.disabled || props.isToggle) {
        return;
    }

    if (isHeld.value) {
        isHeld.value = false;
        try {
            (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
        }
        catch {
            // Ignore release pointer capture errors
        }
        releaseButton(props.widgetId);
        emit('change', 0);
    }
}

function handlePointerCancel(): void {
    if (props.disabled || props.isToggle) {
        return;
    }
    if (isHeld.value) {
        isHeld.value = false;
        releaseButton(props.widgetId);
        emit('change', 0);
    }
}
</script>

<template>
    <button
        type="button"
        class="dancefloor-btn"
        :class="{
            'dancefloor-btn--active': isActive,
            'dancefloor-btn--held': isHeld,
            'dancefloor-btn--disabled': disabled,
        }"
        :style="{
            '--btn-accent-color': color,
        }"
        :disabled="disabled"
        @click="handleClick"
        @pointerdown="handlePointerDown"
        @pointerup="handlePointerUp"
        @pointercancel="handlePointerCancel"
    >
        <span class="btn-indicator" />
        <v-icon
            v-if="icon"
            class="btn-icon"
            :icon="icon"
        />
        <span class="btn-label">{{ label }}</span>
        <span
            v-if="isToggle"
            class="btn-mode-badge"
        >
            {{ isActive ? "AAN" : "UIT" }}
        </span>
        <span
            v-else
            class="btn-mode-badge"
        >
            FLASH
        </span>
    </button>
</template>

<style scoped>
.dancefloor-btn {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 84px;
    padding: 12px 14px;
    background: linear-gradient(145deg, #18182b, #121220);
    border: 2px solid rgba(255, 255, 255, 0.12);
    border-radius: 14px;
    color: #ffffff;
    cursor: pointer;
    user-select: none;
    touch-action: manipulation;
    transition: all 0.12s ease-out;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.45);
    overflow: hidden;
}

.dancefloor-btn:active,
.dancefloor-btn--held {
    transform: scale(0.97);
}

.dancefloor-btn--active {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(0, 0, 0, 0.3)), #1c1c34;
    border-color: var(--btn-accent-color);
    box-shadow: 0 0 20px var(--btn-accent-color), inset 0 0 14px rgba(255, 255, 255, 0.15);
}

.btn-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    transition: all 0.15s ease;
}

.dancefloor-btn--active .btn-indicator {
    background-color: var(--btn-accent-color);
    box-shadow: 0 0 8px var(--btn-accent-color);
}

.btn-icon {
    font-size: 28px;
    margin-bottom: 4px;
    color: #ffffff;
}

.dancefloor-btn--active .btn-icon {
    color: var(--btn-accent-color);
    text-shadow: 0 0 10px var(--btn-accent-color);
}

.btn-label {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    text-align: center;
    line-height: 1.2;
    word-break: break-word;
    color: #f0f0ff;
}

.dancefloor-btn--active .btn-label {
    color: #ffffff;
    text-shadow: 0 0 8px var(--btn-accent-color);
}

.btn-mode-badge {
    margin-top: 4px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1px;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.35);
    color: rgba(255, 255, 255, 0.55);
}

.dancefloor-btn--active .btn-mode-badge {
    background: var(--btn-accent-color);
    color: #0a0a14;
}

.dancefloor-btn--disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
}
</style>
